import { dynamicFormApiConnector } from 'apiConnector/form/dynamic';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import { ofType } from 'redux-observable';
import type { SchedulerLike } from 'rxjs';
import { of } from 'rxjs';
import { catchError, filter, mergeMap, takeUntil, timeoutWith, withLatestFrom } from 'rxjs/operators';
import { defaultFormReceivedThunk } from 'state/dynamic/dynamicForm.thunks';
import { commonFormActions } from 'state/forms/actions/form';
import { commonQuoteAction } from 'state/forms/actions/quote';
import type { Dispatchable, EpicWithThunk } from 'state/state.common';
import type { WorkspaceTabQuoteRepriced } from 'state/workspaces/workspace.actions';
import { getTodayStringInLocalTimezone } from 'utils/core/date';

import type { ReloadDynamicFormRequest } from './fetchDynamicFormWhenQuoteRepriceEpic';

function buildReloadDynamicFormRequest(
  { tabId, id, idType, rfqId, pricerRfqId, processingChain }: WorkspaceTabQuoteRepriced,
  isPrivateMode: boolean,
) {
  const tradeDate = getTodayStringInLocalTimezone();

  return {
    tabId,
    id,
    idType,
    tradeDate,
    quoteId: rfqId,
    isPrivateMode,
    pricerRfqId,
    processingChain,
  } as ReloadDynamicFormRequest;
}

export function fetchDynamicFormWhenQuoteRepriceWithLastQuoteEpic(
  timeoutInMillis: number = 60000,
  appScheduler?: SchedulerLike,
): EpicWithThunk {
  return (action$, state$, { oldHttpClient, selectors: { isUserPreferencesPrivateMode } }) =>
    action$.pipe(
      ofType('WORKSPACE_TAB_QUOTE_REPRICED'),
      filter(action => action.workspace === 'dynamic'),
      withLatestFrom(state$),
      mergeMap(([action, state]) => {
        const { tabId, rfqId, withLastQuote } = action;
        const tabRemoved$ = tabRemoved(action$, tabId, state$);
        const isPrivateMode = isUserPreferencesPrivateMode(state);
        const api = dynamicFormApiConnector(oldHttpClient);
        const request = buildReloadDynamicFormRequest(action, isPrivateMode);

        return api
          .getDynamicFormByIdType(request)
          .pipe(
            mergeMap(dynamicForm => {
              const {
                values,
                blocksMetadata,
                fieldsMetadata,
                productDescription,
                version,
                actions,
                quoteQuery,
                errors,
                negotiationId,
                allowedRequests,
              } = dynamicForm;

              const result: Dispatchable[] = [
                defaultFormReceivedThunk(
                  tabId,
                  blocksMetadata,
                  fieldsMetadata,
                  productDescription,
                  values,
                  version,
                  negotiationId,
                  actions,
                  quoteQuery,
                  allowedRequests,
                  errors,
                ),
              ];

              if (withLastQuote) {
                result.push(commonQuoteAction.previousQuoteLoadRequested('dynamic', tabId, rfqId));
              }

              return of(...result);
            }),
            catchError(error =>
              of(
                commonFormActions.defaultFormFailed(
                  'dynamic',
                  tabId,
                  error.message || 'repriced failed',
                  error.traceId,
                ),
              ),
            ),
            timeoutWith(timeoutInMillis, of(commonFormActions.defaultFormTimeouted('dynamic', tabId)), appScheduler),
          )
          .pipe(takeUntil(tabRemoved$));
      }),
    );
}
